<template>
    <a-table
        :columns="clinicSurveyColumns"
        :data-source="surveyLabData"
        :row-class-name="setRowClassName"
        :custom-row="customRow"
        :pagination="tablePagination"
        class="survey-table"
        row-key="id"
        @change="onTableChange"
    >
        <template #expandedRowRender="{ record }">
            <a-table
                v-if="record.surveys"
                :columns="innerSurveyColumns"
                :data-source="record.surveys"
                :pagination="false"
            >
                <template #userCreate="{ record: subRecord }">
                    <span>
                        <a-tag color="blue">
                            {{ subRecord.userCreate
                                ? subRecord.userCreate
                                : (subRecord.autoTransferred ? 'Автоматически' : null)
                            }}
                        </a-tag>
                    </span>
                </template>
            </a-table>
        </template>
        <template #userCreate="{ record }">
            <span>
                <a-tag
                    v-if="record.userCreate"
                    color="blue"
                >
                    {{ record.userCreate }}
                </a-tag>
            </span>
        </template>
        <template #userEdit="{ record }">
            <span>
                <a-tag
                    v-if="record.userEdit"
                    color="blue"
                >
                    {{ record.userEdit }}
                </a-tag>
            </span>
        </template>
    </a-table>
</template>

<script>
import { defineComponent } from 'vue';
import { PAGE_NAMES } from '@/composables/useBackState';
import { savePageBackState } from '@/utils/backNavigation';

export default defineComponent({
    props: {
        patientId: {
            type: Number,
            required: true,
        },
        surveyBaseUrl: {
            type: String,
            required: true,
        },
        backNavigationContext: {
            type: Object,
            default: null,
        },
        restoreState: {
            type: Object,
            default: null,
        },
        sourcePageName: {
            type: String,
            default: PAGE_NAMES.PATIENT_CARD,
        },
    },

    emits: [
        'ready-loaded',
        'restored',
    ],

    data() {
        return {
            clinicSurveyColumns: [{
                title: 'Исследование',
                width: 300,
                key: 'surveyType',
                dataIndex: 'surveyType',
            }, {
                title: 'Дата проведения',
                width: 100,
                key: 'dateSurveyRu',
                dataIndex: 'dateSurveyRu',
            }, {
                title: 'Создал',
                width: 200,
                key: 'userCreate',
                dataIndex: 'userCreate',
                slots: { customRender: 'userCreate' },
            }, {
                title: 'Изменил',
                width: 200,
                key: 'userEdit',
                dataIndex: 'userEdit',
                slots: { customRender: 'userEdit' },
            }],
            innerSurveyColumns: [
                { title: 'Название исследования', dataIndex: 'surveyType' },
                { title: 'Дата создания', dataIndex: 'curDateRu' },
                { title: 'Создал', dataIndex: 'userCreate', slots: { customRender: 'userCreate' } },
            ],
            surveyLabData: [],
            highlightedRowId: null,
            tablePagination: {
                current: 1,
                pageSize: 10,
                showSizeChanger: false,
            },
        };
    },

    watch: {
        patientId(val) {
            this.getSurveys(val);
        },
        restoreState(val) {
            if (val && this.surveyLabData.length) {
                this.applyRestoreState(val);
            }
        },
    },
    mounted() {
        this.getSurveys(this.patientId);
    },
    methods: {
        getScrollContainer() {
            return this.$el?.closest('.main.page')?.querySelector('.ant-layout-content') ?? null;
        },

        getScrollTop() {
            return this.getScrollContainer()?.scrollTop ?? 0;
        },

        setScrollTop(scrollTop) {
            const el = this.getScrollContainer();
            if (el) {
                el.scrollTop = scrollTop;
            }
        },

        buildPageBackData(lastVisitedRowId) {
            return {
                ...(this.backNavigationContext || {}),
                surveys: {
                    scrollTop: this.getScrollTop(),
                    lastVisitedRowId,
                    paginationCurrent: this.tablePagination.current,
                },
            };
        },

        onTableChange(pagination) {
            this.tablePagination = {
                ...this.tablePagination,
                current: pagination.current,
            };
        },

        applyRestoreState(restoreState) {
            if (!restoreState) {
                return;
            }

            if (restoreState.lastVisitedRowId != null) {
                this.highlightedRowId = restoreState.lastVisitedRowId;
            }

            if (typeof restoreState.paginationCurrent === 'number') {
                this.tablePagination = {
                    ...this.tablePagination,
                    current: restoreState.paginationCurrent,
                };
            }

            this.$nextTick(() => {
                if (typeof restoreState.scrollTop === 'number') {
                    this.setScrollTop(restoreState.scrollTop);
                }
                this.scrollToHighlightedRow();
                this.$emit('restored');
            });
        },

        scrollToHighlightedRow() {
            if (this.highlightedRowId === null || !this.$el) {
                return;
            }
            const row = this.$el.querySelector(
                `tr[data-row-key="${this.highlightedRowId}"]`,
            );
            row?.scrollIntoView({ block: 'nearest', behavior: 'auto' });
        },

        getSurveys(id) {
            if (!this.restoreState) {
                this.highlightedRowId = null;
            }

            this.$http.get('/patient/surveys/labs', {
                params: {
                    patientId: id,
                    page: JSON.stringify([0, 50]),
                },
            }).then((response) => {
                this.surveyLabData = response.data.data;
                this.tablePagination = {
                    ...this.tablePagination,
                    total: this.surveyLabData.length,
                };
                if (!this.restoreState) {
                    this.tablePagination.current = 1;
                }
                this.$emit('ready-loaded', true);
                this.$nextTick(() => {
                    if (this.restoreState) {
                        this.applyRestoreState(this.restoreState);
                    }
                });
            });
        },

        setRowClassName(record) {
            const classes = [];

            if (!record.surveys) {
                classes.push('hidden-expander');
            }

            if (this.highlightedRowId !== null
                && String(record.id) === String(this.highlightedRowId)) {
                classes.push('lab-surveys__visited-row');
            }

            return classes.join(' ');
        },

        customRow(record) {
            return {
                onDblclick: (e) => {
                    if (e.target.classList.contains('ant-table-row-expand-icon')) {
                        e.preventDefault();
                        return;
                    }

                    if (!this.backNavigationContext) {
                        this.$router.push({ path: `${this.surveyBaseUrl}${record.id}` });
                        return;
                    }

                    savePageBackState(
                        this.$route.fullPath || '/',
                        this.sourcePageName,
                        this.buildPageBackData(record.id),
                    );

                    this.$router.push({ path: `${this.surveyBaseUrl}${record.id}` });
                },
            };
        },
    },
});
</script>

<style lang="scss" scoped>
div.survey-table {
    width: calc(100vw - 335px);
}
</style>

<style lang="scss">
.survey-table .lab-surveys__visited-row > td {
    background-color: #d6f39f !important;
}
</style>
